import classNames from 'classnames';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons/faInfoCircle';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormattedMessage } from 'react-intl';
import IconPopover from '@/components/IconPopover/IconPopover';
import isEqual from 'react-fast-compare';
import Rankings from './Rankings';
import RatingDisplaySmall from '@/components/RatingDisplaySmall/RatingDisplaySmall';
import RatingsCounter from './RatingsCounter';
import React from 'react';
import styles from './RatingsSummary.module.scss';
import TopBadge from '@/components/Badges/TopBadge';
import { truncateToOneDecimalWithoutRounding } from '@/utils/truncateToOneDecimalWithoutRounding';

type Props = {
    fullPage: boolean;
    mobile: boolean;
    modal?: boolean;
    percentiles: any;
    ratings: any;
    seller: any;
    updateFilterFromBarChart: Function;
};

const RatingsSummary = (props: Props) => {
    const { fullPage, mobile, modal, percentiles, ratings, seller, updateFilterFromBarChart } = props;
    const isTopRated = Boolean(percentiles.isTopRated);
    const overall = Boolean(ratings?.overall) ? truncateToOneDecimalWithoutRounding(ratings.overall) : 0;
    const counterStyles = modal ? classNames(styles.ratingsCounter, styles.modalCounter) : styles.ratingsCounter;
    const overallTextStyles = modal ? classNames(styles.overallText, styles.modalOverallText) : styles.overallText;
    const topStyles = modal ? classNames(styles.top, styles.modalTop) : styles.top;
    const starSize = modal ? '20px' : '24px';

    const showRankings = ratings.totalReviews >= 25;
    const ratingsDisplay = ratings.totalReviews >= 5 ? ratings.overall : 0;

    const infoIcon = (
        <IconPopover
            icon={<FontAwesomeIcon icon={faInfoCircle} />}
            iconClassName={styles.info}
            id="rating-summary-popover"
            text={<FormattedMessage id="review.ratings_explanation" />}
        />
    );

    const overallRatingLabel =
        ratings.totalReviews >= 5 ? (
            <div className={overallTextStyles}>
                <FormattedMessage id="review.overall_rating_label" values={{ rating: overall }} />
                {!modal && infoIcon}
            </div>
        ) : (
            <div className={overallTextStyles}>
                <FormattedMessage id="review.not_enough_data" />
                {!modal && infoIcon}
            </div>
        );

    const overview = (
        <div className={styles.overview}>
            <div>
                {isTopRated && (
                    <TopBadge badgeStyle={topStyles} className={styles.topBadgeWrapper} imageSize={styles.topBadge} />
                )}
            </div>
            <div>
                <RatingDisplaySmall hideReviewCount rating={ratingsDisplay} starSize={starSize} />
            </div>
            {overallRatingLabel}
        </div>
    );

    return (
        <>
            <div className={styles.innerContainer}>
                {overview}
                <div className={counterStyles}>
                    <RatingsCounter
                        fullPage={fullPage}
                        ratings={ratings}
                        seller={seller}
                        updateFilter={updateFilterFromBarChart}
                    />
                </div>
                <div className={styles.rankings}>
                    {Boolean(showRankings) && <Rankings mobile={mobile} modal={modal} percentiles={percentiles} />}
                </div>
            </div>
        </>
    );
};

export default React.memo<Props>(RatingsSummary, isEqual);
